import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { getUserLabel } from "../../Users/util";
import { useSailors } from "../../Sailors/hooks/useSailors";
import { useAddAgreementSailor } from "./useAddAgreementSailor";

/**
 * Pick an existing sailor to add as the second party to an agreement.
 * Deliberately not a <form>: a nested submit would bubble through the React
 * tree to any Formik form the dialog is rendered inside.
 */
export function AddAgreementSailorDialog({
  open,
  onClose,
  agreementId,
  excludeSailorIds = [],
}) {
  const { sailors, loading } = useSailors();
  const { addAgreementSailor, saving } = useAddAgreementSailor();
  const [sailor, setSailor] = useState(null);

  const options = useMemo(
    () => sailors.filter((s) => !excludeSailorIds.includes(s.id)),
    [sailors, excludeSailorIds]
  );

  const handleClose = () => {
    if (saving) {
      return;
    }
    setSailor(null);
    onClose();
  };

  const handleAdd = async () => {
    if (!sailor) {
      return;
    }
    const ok = await addAgreementSailor({ agreementId, sailorId: sailor.id });
    if (ok) {
      setSailor(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add joint sailor</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={options}
          loading={loading}
          value={sailor}
          onChange={(_, value) => setSailor(value)}
          getOptionLabel={(option) => getUserLabel(option)}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => (
            <TextField {...params} label="Sailor" margin="normal" />
          )}
        />
        <Alert severity="info" sx={{ mt: 1 }}>
          The sailor becomes a party to this agreement and is included in
          credit reporting (CAIS) as party 2 from now on. The original
          application is not changed. This cannot be undone from the portal.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!sailor || saving}
          startIcon={saving ? <CircularProgress size={18} /> : null}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
